import type { HrsFitResult } from "@shared/hrsRackConfigurator";
import clarityLogoSvg from "@/assets/clarity-logo-light.svg?raw";

export type HrsRackPdfSlot = {
  rack: number;
  level: number;
  baseSku: string;
  openTop: boolean;
  name: string;
  spacingIn: number;
  usableHeightIn: number | null;
  solidBrace: boolean;
  widthIn: number | null;
  depthIn: number | null;
  heightIn: number | null;
  weightKg: number | null;
  fit: HrsFitResult;
};

export type HrsRackPdfInput = {
  series: string;
  finish: string;
  configurationCode: string;
  rackLevels: number[];
  shelfSku: string;
  shelfWidthIn: number;
  shelfDepthIn: number;
  shelfQuantity: number;
  solidBracePairCount: number;
  configuredPrice: string | null;
  slots: HrsRackPdfSlot[];
  drawingElement?: HTMLElement | null;
};

function safeFilePart(value: string) {
  return value.replace(/[^a-z0-9-]+/gi, "-").replace(/-+/g, "-");
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not render image."));
    image.src = src;
  });
}

async function addClarityWatermark(
  pdf: InstanceType<typeof import("jspdf").jsPDF>
) {
  const source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(clarityLogoSvg)}`;
  const image = await loadImage(source);
  const canvas = document.createElement("canvas");
  const aspect = image.naturalWidth / image.naturalHeight || 2.4;
  canvas.width = 1200;
  canvas.height = Math.round(canvas.width / aspect);
  const context = canvas.getContext("2d");
  if (!context) return;
  context.globalAlpha = 0.22;
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const widthMm = 38;
  pdf.addImage(
    canvas.toDataURL("image/png"),
    "PNG",
    194 - widthMm,
    9,
    widthMm,
    widthMm / aspect
  );
}

async function addCapturedRackDrawing(
  pdf: InstanceType<typeof import("jspdf").jsPDF>,
  element: HTMLElement,
  top: number
) {
  const { default: html2canvas } = await import("html2canvas");
  const canvas = await html2canvas(element, {
    backgroundColor: "#f8f8f5",
    logging: false,
    scale: 2,
    useCORS: true,
  });
  const maxWidth = 178;
  const maxHeight = 105;
  const scale = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
  const width = canvas.width * scale;
  const height = canvas.height * scale;
  pdf.addImage(
    canvas.toDataURL("image/jpeg", 0.92),
    "JPEG",
    (210 - width) / 2,
    top,
    width,
    height
  );
  return height;
}

function addRackDrawing(
  pdf: InstanceType<typeof import("jspdf").jsPDF>,
  input: HrsRackPdfInput,
  top: number
) {
  const availableWidth = 174;
  const floor = top + 82;
  const rackHeightInches = input.rackLevels.map((levels, rackIndex) => {
    const rackSlots = input.slots.filter(slot => slot.rack === rackIndex + 1);
    const enclosedHeight = rackSlots
      .filter(slot => !slot.openTop)
      .reduce((sum, slot) => sum + slot.spacingIn, 0);
    const topEquipmentHeight =
      rackSlots.find(slot => slot.openTop)?.heightIn ?? 4;
    return enclosedHeight + topEquipmentHeight;
  });
  const totalRackWidthInches = input.shelfWidthIn * input.rackLevels.length;
  const mmPerInch = Math.min(
    68 / Math.max(1, ...rackHeightInches),
    availableWidth / Math.max(1, totalRackWidthInches)
  );
  const bayWidth = input.shelfWidthIn * mmPerInch;
  const totalWidth = bayWidth * input.rackLevels.length;
  const left = (210 - totalWidth) / 2;
  pdf.setDrawColor(25, 30, 32);
  pdf.setFillColor(37, 43, 46);
  pdf.setLineWidth(0.8);
  pdf.line(left - 2, floor + 4, left + totalWidth + 2, floor + 4);

  input.rackLevels.forEach((levels, rackIndex) => {
    const x = left + rackIndex * bayWidth;
    const rackSlots = input.slots.filter(slot => slot.rack === rackIndex + 1);
    let shelfY = floor;
    const topShelfY =
      floor -
      rackSlots
        .filter(slot => !slot.openTop)
        .reduce((sum, slot) => sum + slot.spacingIn * mmPerInch, 0);
    pdf.rect(x, topShelfY, 2, floor - topShelfY + 2, "F");
    pdf.rect(x + bayWidth - 2, topShelfY, 2, floor - topShelfY + 2, "F");
    for (let level = 1; level <= levels; level += 1) {
      pdf.rect(x + 1, shelfY - 2, bayWidth - 2, 2, "F");
      const slot = rackSlots.find(candidate => candidate.level === level);
      if (slot?.solidBrace) {
        pdf.setFillColor(183, 121, 31);
        pdf.rect(x + 3, shelfY, bayWidth - 6, 0.9, "F");
        pdf.setFillColor(37, 43, 46);
      }
      if (slot?.name) {
        const equipmentHeight = Math.max(1.5, (slot.heightIn ?? 2) * mmPerInch);
        const shelfWidth = bayWidth - 2;
        const equipmentWidth = Math.max(
          1.5,
          ((slot.widthIn ?? input.shelfWidthIn * 0.72) / input.shelfWidthIn) *
            shelfWidth
        );
        const equipmentX = x + (bayWidth - equipmentWidth) / 2;
        pdf.setFillColor(244, 244, 241);
        pdf.setDrawColor(
          slot.fit.status === "does_not_fit" ? 220 : 25,
          slot.fit.status === "does_not_fit" ? 38 : 30,
          slot.fit.status === "does_not_fit" ? 38 : 32
        );
        pdf.rect(
          equipmentX,
          shelfY - 2 - equipmentHeight,
          equipmentWidth,
          equipmentHeight,
          "FD"
        );
        pdf.setTextColor(30, 41, 59);
        pdf.setFontSize(5.5);
        const labelLines = pdf.splitTextToSize(
          slot.name,
          Math.max(10, equipmentWidth - 2)
        ) as string[];
        pdf.text(
          labelLines,
          x + bayWidth / 2,
          shelfY - 3.5 - equipmentHeight - (labelLines.length - 1) * 2.2,
          { align: "center", maxWidth: Math.max(10, equipmentWidth - 2) }
        );
        pdf.setFillColor(37, 43, 46);
        pdf.setDrawColor(25, 30, 32);
      }
      if (slot && !slot.openTop) shelfY -= slot.spacingIn * mmPerInch;
    }
    pdf.setTextColor(51, 65, 85);
    pdf.setFontSize(7);
    pdf.text(`Rack ${rackIndex + 1}`, x + bayWidth / 2, floor + 9, {
      align: "center",
    });
  });
}

export async function buildHrsRackConfigurationPdf(input: HrsRackPdfInput) {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const bottom = 282;
  let y = 16;

  const ensureRoom = (height: number) => {
    if (y + height <= bottom) return;
    pdf.addPage();
    y = 16;
  };
  const text = (value: string, size = 9, bold = false) => {
    ensureRoom(size * 0.6 + 3);
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(size);
    pdf.setTextColor(31, 41, 55);
    const lines = pdf.splitTextToSize(value, pageWidth - 32) as string[];
    pdf.text(lines, 16, y);
    y += lines.length * size * 0.42 + 2;
  };

  await addClarityWatermark(pdf).catch(() => undefined);

  text("HRS Rack Configuration", 18, true);
  text(
    `Configuration downloaded on ${new Intl.DateTimeFormat("en-SG", {
      timeZone: "Asia/Singapore",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date())} SGT`,
    8
  );
  text(
    `${input.series}-${input.configurationCode} · ${input.finish}`,
    11,
    true
  );
  if (input.configuredPrice) {
    text(
      `Configured price: ${input.configuredPrice} (SGD, GST included)`,
      11,
      true
    );
  }
  if (input.drawingElement) {
    try {
      const drawingHeight = await addCapturedRackDrawing(
        pdf,
        input.drawingElement,
        y + 3
      );
      y += drawingHeight + 10;
    } catch {
      addRackDrawing(pdf, input, y + 3);
      y += 98;
    }
  } else {
    addRackDrawing(pdf, input, y + 3);
    y += 98;
  }

  text("Configuration", 13, true);
  text(
    `${input.rackLevels.length}-wide joined system · ${input.rackLevels.map((levels, index) => `Rack ${index + 1}: ${levels} level${levels === 1 ? "" : "s"}`).join(" · ")}`
  );
  text(
    `${input.shelfSku} · ${input.shelfWidthIn}\" W × ${input.shelfDepthIn}\" D`
  );
  if (input.solidBracePairCount > 0) {
    text(`SXR Solid Brace Insert (1 pair) × ${input.solidBracePairCount}`);
  }
  if (input.series === "SXR") {
    for (const spacing of [12, 14, 16]) {
      const count = input.slots.filter(
        slot => !slot.openTop && slot.spacingIn === spacing
      ).length;
      if (count > 0) {
        text(`SXR ${spacing}\" extended spacing (6 posts) × ${count}`);
      }
    }
  }

  text("Equipment schedule", 13, true);
  for (const slot of input.slots) {
    ensureRoom(18);
    const dimensions = [
      slot.widthIn == null ? null : `${Math.round(slot.widthIn)}\" W`,
      slot.depthIn == null ? null : `${Math.round(slot.depthIn)}\" D`,
      slot.heightIn == null ? null : `${Math.round(slot.heightIn)}\" H`,
      slot.weightKg == null ? null : `${Math.round(slot.weightKg)} kg`,
    ]
      .filter(Boolean)
      .join(" × ");
    text(
      `Rack ${slot.rack} · Level ${slot.level}: ${slot.name || "Equipment not specified"}`,
      9,
      true
    );
    text(
      `${slot.baseSku} · ${dimensions || "Dimensions not specified"} · ${
        slot.openTop
          ? "Open top - no height limit"
          : slot.usableHeightIn != null &&
              slot.usableHeightIn !== slot.spacingIn
            ? `Selected opening ${slot.spacingIn}\" · ${slot.usableHeightIn.toFixed(2)}\" usable with brace above`
            : `Selected opening ${slot.spacingIn}\"`
      }${slot.solidBrace ? " · Solid Brace Insert pair below this shelf" : ""}`,
      8
    );
    const fitText =
      slot.fit.status === "fits"
        ? "Fit checked — suitable"
        : slot.fit.status === "does_not_fit"
          ? `Fit issue — ${slot.fit.issues.join(" ")}`
          : slot.fit.status === "review"
            ? "Fit check incomplete"
            : "Fit not checked";
    text(fitText, 8);
  }

  text("Planning note", 10, true);
  text(
    "This drawing and fit check use nominal dimensions for planning. Confirm final clearances, ventilation and load capacity before ordering.",
    8
  );
  return pdf;
}

export async function downloadHrsRackConfigurationPdf(input: HrsRackPdfInput) {
  const pdf = await buildHrsRackConfigurationPdf(input);
  pdf.save(
    `${safeFilePart(input.series)}-${safeFilePart(input.configurationCode)}-rack-configuration.pdf`
  );
}
